import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Clock, 
  Zap, 
  CheckCircle, 
  Phone, 
  ChevronRight,
  Star,
  MapPin,
  Calendar,
  Users
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      if (user.user_type === "nurse") {
        navigate("/nurse/dashboard");
      } else if (user.user_type === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/register");
    }
  };

  const handleSeeNurses = () => {
    // Logged-in patient: go straight to the care request flow (same as "Nouvelle demande de soin")
    if (user && user.user_type === "patient") {
      navigate("/care-request");
    } else if (user && user.user_type === "nurse") {
      navigate("/nurse/dashboard");
    } else if (user && user.user_type === "admin") {
      navigate("/admin");
    } else {
      navigate("/search");
    }
  };

  const features = [
    {
      icon: Shield,
      title: "Qualité",
      subtitle: "Excellence & Éthique",
      description: "Vérification rigoureuse des diplômes RPPS, charte éthique, évaluation continue."
    },
    {
      icon: Clock,
      title: "Flexibilité",
      subtitle: "Service 24/7",
      description: "Planification précise, autonomie des infirmiers, disponibilité permanente."
    },
    {
      icon: Zap,
      title: "Rapidité",
      subtitle: "Matching Intelligent",
      description: "Géolocalisation, mise en relation instantanée, confirmation sécurisée."
    }
  ];

  const steps = [
    { number: "01", title: "Décrivez votre besoin", description: "Type de soin, adresse, date et heure souhaitées" },
    { number: "02", title: "Matching intelligent", description: "Trouvez l'infirmier idéal selon vos critères" },
    { number: "03", title: "Confirmation sécurisée", description: "Paiement en ligne, intervention confirmée" },
    { number: "04", title: "Soin à domicile", description: "L'infirmier intervient chez vous" }
  ];

  const rates = [
    { label: "Intervention standard", price: "20 €" },
    { label: "Majoration nuit (20h-7h)", price: "+10 €" },
    { label: "Weekend / Jours fériés", price: "+10 €" },
    { label: "Nuit + Week-end", price: "40 €" }
  ];

  const nurseProfiles = [
    { icon: "🩺", title: "Infirmiers libéraux", desc: "Développez votre patientèle" },
    { icon: "💼", title: "Salariés en reconversion", desc: "Lancez votre activité libérale" },
    { icon: "🌟", title: "Jeunes diplômés", desc: "Démarrez en toute sérénité" },
    { icon: "🏡", title: "Retraités actifs", desc: "Exercez à votre rythme" }
  ];

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container-ip">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#fonctionnement" className="nav-link">Fonctionnement</a>
              <a href="#infirmiers" className="nav-link">Infirmiers</a>
              <a href="#contact" className="nav-link">Contact</a>
            </div>

            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={handleGetStarted}
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Mon espace
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-white hover:text-[#00E073]" data-testid="login-btn">
                      Connexion
                    </Button>
                  </Link>
                  <Button onClick={handleGetStarted} className="btn-primary" data-testid="get-started-btn">
                    Commencer
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="container-ip">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-[#00E073]/10 border border-[#00E073]/30 rounded-full px-4 py-2 mb-6">
                <Star className="w-4 h-4 text-[#00E073]" />
                <span className="text-sm text-[#00E073] font-medium">Soins infirmiers premium à domicile</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Le nouveau standard des{" "}
                <span className="gradient-text">soins infirmiers</span>{" "}
                à domicile
              </h1>
              
              <p className="text-lg text-[#94A3B8] mb-8 max-w-xl">
                Plateforme premium d'intermédiation entre patients et infirmiers libéraux indépendants. 
                Qualité, flexibilité, rapidité.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button onClick={handleSeeNurses} className="btn-primary text-lg px-8 py-4" data-testid="hero-cta-btn">
                  Voir les infirmiers disponibles
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/register?type=nurse">
                  <Button variant="outline" className="btn-outline text-lg px-8 py-4">
                    Je suis infirmier
                  </Button>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 text-sm text-[#64748B]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00E073]" />
                  <span>RPPS vérifié</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00E073]" />
                  <span>Paiement sécurisé</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00E073]" />
                  <span>RGPD conforme</span>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in stagger-2">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80" 
                  alt="Infirmière professionnelle" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-[#0D1C32]" id="fonctionnement">
        <div className="container-ip">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pourquoi choisir Infirmiers Privés ?
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Une plateforme conçue par des professionnels de santé, pour des soins d'excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card-dark p-8 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-[#00E073]/10 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#00E073]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-[#00E073] text-sm font-medium mb-3">{feature.subtitle}</p>
                <p className="text-[#94A3B8]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-padding bg-[#1F2A38]/30">
        <div className="container-ip">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              En quelques étapes simples, trouvez l'infirmier idéal pour vos soins à domicile.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-[#00E073]/10 mb-4">{step.number}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-[#94A3B8]">{step.description}</p>
                {index < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-3 w-6 h-6 text-[#2A3A4D]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA for nurses */}
      <section className="section-padding bg-gradient-to-r from-[#00E073]/10 to-[#28CAD4]/10" id="infirmiers">
        <div className="container-ip">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Vous êtes infirmier ?
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Que vous soyez libéral, salarié souhaitant vous lancer, jeune diplômé ou retraité actif,
              rejoignez notre réseau et exercez en toute indépendance.
            </p>
          </div>

          {/* Nurse profiles */}
          <div className="grid md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
            {nurseProfiles.map((profile, index) => (
              <div key={index} className="bg-[#1F2A38] border border-[#2A3A4D] rounded-xl p-5 text-center hover:border-[#00E073]/30 transition-colors">
                <span className="text-3xl mb-3 block">{profile.icon}</span>
                <h3 className="text-white font-semibold mb-1">{profile.title}</h3>
                <p className="text-sm text-[#94A3B8]">{profile.desc}</p>
              </div>
            ))}
          </div>

          {/* Accompagnement */}
          <div className="card-dark p-8 max-w-3xl mx-auto mb-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#00E073]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-[#00E073]" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Vous n'êtes pas encore libéral ?
                </h3>
                <p className="text-[#94A3B8] mb-4">
                  Nous vous accompagnons dans la création de votre structure. 
                  Démarches administratives, choix du statut, inscription RPPS... 
                  Notre équipe vous guide à chaque étape pour lancer votre activité libérale sereinement.
                </p>
                <Link to="/register?type=nurse">
                  <Button className="btn-outline">
                    En savoir plus sur l'accompagnement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/register?type=nurse">
              <Button className="btn-primary text-lg px-8">
                Rejoindre le réseau
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-3xl mx-auto">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-[#00E073] mx-auto mb-3" />
              <p className="text-white font-medium">Choisissez vos zones</p>
            </div>
            <div className="text-center">
              <Calendar className="w-8 h-8 text-[#00E073] mx-auto mb-3" />
              <p className="text-white font-medium">Gérez vos disponibilités</p>
            </div>
            <div className="text-center">
              <Shield className="w-8 h-8 text-[#00E073] mx-auto mb-3" />
              <p className="text-white font-medium">Restez indépendant</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / Hotline */}
      <section className="section-padding bg-[#0D1C32]" id="contact">
        <div className="container-ip">
          <div className="card-dark p-8 md:p-12 text-center">
            <Phone className="w-12 h-12 text-[#00E073] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Besoin d'aide ?
            </h2>
            <p className="text-[#94A3B8] mb-6">
              Notre équipe est disponible du lundi au vendredi, 9h-18h
            </p>
            <Button className="btn-primary">
              <Phone className="w-4 h-4 mr-2" />
              Contacter le support
            </Button>
            
            <p className="text-sm text-[#64748B] mt-6">
              En cas d'urgence médicale, composez le 15 (SAMU)
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F2A38] border-t border-[#2A3A4D] py-12">
        <div className="container-ip">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-10 mb-4" />
              <p className="text-sm text-[#94A3B8]">
                Plateforme premium d'intermédiation entre patients et infirmiers libéraux indépendants.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Patients</h4>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li><Link to="/register" className="hover:text-[#00E073]">Créer un compte</Link></li>
                <li><a href="#fonctionnement" className="hover:text-[#00E073]">Comment ça marche</a></li>
                <li><Link to="/search" className="hover:text-[#00E073]">Rechercher un infirmier</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Infirmiers</h4>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li><Link to="/register?type=nurse" className="hover:text-[#00E073]">Rejoindre le réseau</Link></li>
                <li><a href="#infirmiers" className="hover:text-[#00E073]">Devenir libéral</a></li>
                <li><a href="#" className="hover:text-[#00E073]">FAQ Infirmiers</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li><a href="#" className="hover:text-[#00E073]">CGU</a></li>
                <li><Link to="/politique-confidentialite" className="hover:text-[#00E073]" data-testid="footer-privacy-link">Politique de confidentialité</Link></li>
                <li><a href="#" className="hover:text-[#00E073]">Mentions légales</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#2A3A4D] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#64748B]">
              © 2024 Infirmiers Privés. Tous droits réservés.
            </p>
            <p className="text-xs text-[#64748B]">
              Plateforme d'intermédiation - N'exerce aucun acte de soin
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
